/**
 * QueuePage — file de publication (S5b).
 *
 * Vue par jour groupée par plateforme. Chaque ligne est une row
 * `queue_items` que le scheduler worker consomme via MockAdapter
 * (en S5b ; adapters réels en S6+).
 *
 * Drag-drop (`@dnd-kit/core`) → réordonne les jobs `scheduled` par
 * priorité (UPDATE serveur via `useReorderQueueItem`).
 *
 * Click sur une ligne → ouvre `<Drawer>` détail (queue_item +
 * historique des `publication_attempts`).
 */
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { Calendar, List, Pause, Play, RefreshCw, Send, Trash2, Zap } from 'lucide-react'
import { toast } from 'sonner'
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import Button from '@/components/ui/Button'
import Drawer from '@/components/ui/Drawer'
import EmptyState from '@/components/ui/EmptyState'
import PlatformDot from '@/components/ui/PlatformDot'
import QueueCalendarView from '@/components/queue/QueueCalendarView'
import Page from '@/components/layout/Page'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import {
  useDeleteQueueItem,
  useForcePublishNow,
  usePauseQueueItem,
  useQueueItemDetail,
  useQueueList,
  useReorderQueueItem,
  useResumeQueueItem,
} from '@/hooks/useQueue'
import type { QueueAttemptResponse, QueueItemResponse } from '@/types/queue'

import './QueuePage.css'

const STATUS_FR: Record<QueueItemResponse['status'], string> = {
  scheduled: 'Planifié',
  running: 'En cours',
  done: 'Publié',
  failed: 'Échec',
  paused: 'En pause',
}

// Libellés FR pour les `draft_kind` techniques (suffixe technique
// inutilisable pour Stéphanie). Fallback sur la valeur brute si on n'a
// pas mappé — la liste suit l'enum `DraftKind` côté BE.
const DRAFT_KIND_FR: Record<string, string> = {
  etsy_listing_physical: 'Listing Etsy (physique)',
  etsy_listing_digital: 'Listing Etsy (téléchargement)',
  pinterest_pin: 'Pin Pinterest',
  instagram_post: 'Post Instagram',
  instagram_reel: 'Reel Instagram',
  tiktok_post: 'Post TikTok',
  tiktok_slideshow: 'Slideshow TikTok',
  saatchi_listing: 'Listing Saatchi',
  artfinder_listing: 'Listing Artfinder',
}

const JOB_KIND_FR: Record<QueueItemResponse['job_kind'], string> = {
  publish_draft: 'Publication',
  regenerate_deliverables: 'Régénération livrables',
  gelato_catalog_refresh: 'Catalogue Gelato',
  update_publication: 'Mise à jour publication',
  end_publication: 'Retrait publication',
  renew_publication: 'Renouvellement publication',
  delete_publication: 'Suppression publication',
  render_video: 'Rendu vidéo',
  prepare_pod: 'Préparation POD',
}

function formatScheduledFor(iso: string): string {
  const date = new Date(iso)
  const now = new Date()
  const deltaMs = date.getTime() - now.getTime()
  const absMin = Math.round(Math.abs(deltaMs) / 60_000)
  if (absMin < 1) return deltaMs >= 0 ? 'imminent' : "à l'instant"
  if (absMin < 60) return deltaMs >= 0 ? `dans ${absMin} min` : `il y a ${absMin} min`
  const sameDay = date.toDateString() === now.toDateString()
  if (sameDay) {
    return `aujourd'hui ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
  }
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function QueuePage() {
  const [params, setParams] = useSearchParams()
  const view = params.get('view') === 'calendar' ? 'calendar' : 'list'
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const list = useQueueList({ limit: 200 })
  const reorder = useReorderQueueItem()
  const pause = usePauseQueueItem()
  const resume = useResumeQueueItem()
  const force = useForcePublishNow()
  const remove = useDeleteQueueItem()
  const { confirm } = useConfirm()

  const items = useMemo(() => list.data?.items ?? [], [list.data])

  // Groupe par platform pour le rendu en colonnes (à terme : par jour ×
  // plateforme — en S5b on simplifie, le scheduling fin arrive S5c).
  const grouped = useMemo(() => {
    const buckets: Record<string, QueueItemResponse[]> = {}
    for (const item of items) {
      // Depuis S2.5e les jobs non-publish (regenerate_deliverables, ...)
      // n'ont pas de `platform`. La page actuelle est orientée publications
      // par canal — on les ignore ici, un onglet dédié pourra arriver.
      if (!item.platform) continue
      const key = item.platform
      if (!buckets[key]) buckets[key] = []
      buckets[key].push(item)
    }
    return buckets
  }, [items])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = (event: DragEndEvent, platform: string) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const platformItems = grouped[platform] ?? []
    const oldIndex = platformItems.findIndex((i) => i.id === active.id)
    const newIndex = platformItems.findIndex((i) => i.id === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    const reordered = arrayMove(platformItems, oldIndex, newIndex)
    // On déduit la nouvelle priorité depuis l'index — simple, lisible :
    // index 0 = priority -10, index 1 = priority 0, etc. Le BE persiste
    // la valeur ; le scheduler consommera l'ordre par priority ASC.
    const targetItem = reordered[newIndex]
    const newPriority = newIndex - 1
    reorder.mutate(
      { id: targetItem.id, priority: newPriority },
      {
        onSuccess: () => toast.success(`File réordonnée (${platform})`),
        onError: () => toast.error('Réordonnancement échoué'),
      },
    )
  }

  if (list.isLoading) {
    return (
      <Page>
        <Page.Header eyebrow="Atelier" title="File de publication" />
        <p className="queue-loading">Chargement…</p>
      </Page>
    )
  }

  if (items.length === 0) {
    return (
      <Page>
        <Page.Header eyebrow="Atelier" title="File de publication" />
        <EmptyState
          icon={Send}
          title="Aucune publication en file"
          description="Approuvez un draft depuis Contenus pour le mettre en file."
        />
      </Page>
    )
  }

  const handleViewToggle = (next: 'list' | 'calendar') => {
    const np = new URLSearchParams(params)
    if (next === 'calendar') np.set('view', 'calendar')
    else np.delete('view')
    setParams(np, { replace: true })
  }

  return (
    <Page width="wide">
      <Page.Header
        eyebrow="Atelier"
        title="File de publication"
        subtitle={`${items.length} job${items.length > 1 ? 's' : ''} en file`}
      />

      <div className="queue-view-toggle">
        <Button
          type="button"
          variant={view === 'list' ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => handleViewToggle('list')}
        >
          <List size={14} aria-hidden="true" />
          Liste
        </Button>
        <Button
          type="button"
          variant={view === 'calendar' ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => handleViewToggle('calendar')}
        >
          <Calendar size={14} aria-hidden="true" />
          Calendrier
        </Button>
      </div>

      {view === 'calendar' ? (
        <QueueCalendarView onSelect={(id) => setSelectedId(id)} />
      ) : (
        <div className="queue-columns">
          {Object.entries(grouped).map(([platform, list]) => (
            <section key={platform} className="queue-column">
              <header className="queue-column-header">
                <PlatformDot platform={platform as never} />
                <h2 className="queue-column-title">{platform}</h2>
                <span className="queue-column-count">{list.length}</span>
              </header>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={(e) => handleDragEnd(e, platform)}
              >
                <SortableContext
                  items={list.map((i) => i.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <ul className="queue-items">
                    {list.map((item) => (
                      <SortableQueueItem
                        key={item.id}
                        item={item}
                        onSelect={() => setSelectedId(item.id)}
                        onPause={() => pause.mutate(item.id)}
                        onResume={() => resume.mutate(item.id)}
                        onForceNow={() => force.mutate(item.id)}
                        onDelete={async () => {
                          const ok = await confirm({
                            title: 'Supprimer ce job de la file ?',
                            message:
                              'Le job et ses tentatives associées seront supprimés définitivement. Les ventes associées (s’il y en a) conservent leur trace.',
                            variant: 'danger',
                            confirmText: 'Supprimer',
                          })
                          if (!ok) return
                          remove.mutate(item.id, {
                            onSuccess: () => toast.success('Job supprimé.'),
                            onError: () => toast.error('Suppression impossible.'),
                          })
                        }}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            </section>
          ))}
        </div>
      )}

      <QueueItemDrawer id={selectedId} onClose={() => setSelectedId(null)} />
    </Page>
  )
}

interface SortableQueueItemProps {
  item: QueueItemResponse
  onSelect: () => void
  onPause: () => void
  onResume: () => void
  onForceNow: () => void
  onDelete: () => void
}

function SortableQueueItem({
  item,
  onSelect,
  onPause,
  onResume,
  onForceNow,
  onDelete,
}: SortableQueueItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const isScheduled = item.status === 'scheduled'
  const isPaused = item.status === 'paused'
  const isFinal = item.status === 'done' || item.status === 'failed'
  // Statuts deletable côté BE : failed, paused, scheduled. `running`/
  // `done` masquent le bouton (un job en cours ne se purge pas, un
  // succès reste dans le journal forensique).
  const isDeletable = item.status === 'failed' || isPaused || isScheduled

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`queue-item queue-item--${item.status}`}
      {...attributes}
      {...listeners}
    >
      <Button
        variant="quiet"
        className="queue-item-body"
        onClick={onSelect}
        aria-label={`Détail ${item.work_title_fr ?? `Œuvre #${item.work_id}`}`}
      >
        <span className="queue-item-title">
          {item.work_title_fr ?? `Œuvre #${item.work_id}`}
          {item.work_variant_label ? (
            <span className="queue-item-variant"> · {item.work_variant_label}</span>
          ) : null}
        </span>
        <span className="queue-item-meta">
          <span className="queue-item-status">{STATUS_FR[item.status]}</span>
          <span className="queue-item-kind">
            {item.draft_kind
              ? (DRAFT_KIND_FR[item.draft_kind] ?? item.draft_kind)
              : JOB_KIND_FR[item.job_kind]}
          </span>
          {!isFinal && (
            <span className="queue-item-time">{formatScheduledFor(item.scheduled_for)}</span>
          )}
          {item.attempts_count > 0 && (
            <span className="queue-item-attempts" title={`${item.attempts_count} tentative(s)`}>
              <RefreshCw size={12} aria-hidden /> {item.attempts_count}
            </span>
          )}
        </span>
        {item.status === 'failed' && item.last_error ? (
          <span className="queue-item-error" title={item.last_error}>
            {item.last_error.length > 140 ? `${item.last_error.slice(0, 140)}…` : item.last_error}
          </span>
        ) : null}
      </Button>

      <div className="queue-item-actions">
        {!isFinal && isScheduled && (
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation()
              onPause()
            }}
            aria-label="Mettre en pause"
          >
            <Pause size={14} aria-hidden />
          </Button>
        )}
        {!isFinal && isPaused && (
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation()
              onResume()
            }}
            aria-label="Reprendre"
          >
            <Play size={14} aria-hidden />
          </Button>
        )}
        {!isFinal && isScheduled && (
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation()
              onForceNow()
            }}
            aria-label="Publier maintenant"
          >
            <Zap size={14} aria-hidden />
          </Button>
        )}
        {isDeletable && (
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
            aria-label="Supprimer ce job"
          >
            <Trash2 size={14} aria-hidden />
          </Button>
        )}
      </div>
    </li>
  )
}

interface QueueItemDrawerProps {
  id: number | null
  onClose: () => void
}

function QueueItemDrawer({ id, onClose }: QueueItemDrawerProps) {
  const detail = useQueueItemDetail(id)
  const item = detail.data?.item
  const attempts = detail.data?.attempts ?? []

  return (
    <Drawer
      isOpen={id !== null}
      onClose={onClose}
      kicker={item ? `${item.platform} · ${STATUS_FR[item.status]}` : undefined}
      title={item ? (item.work_title_fr ?? `Œuvre #${item.work_id}`) : 'Détail'}
      size="comfortable"
    >
      {!item && <p>Chargement…</p>}
      {item && (
        <>
          <Drawer.Section title="Job">
            <dl className="queue-detail-list">
              <dt>Statut</dt>
              <dd>{STATUS_FR[item.status]}</dd>
              <dt>Plateforme</dt>
              <dd>{item.platform}</dd>
              <dt>Variante draft</dt>
              <dd>{item.draft_kind ?? '—'}</dd>
              <dt>Tentatives</dt>
              <dd>{item.attempts_count}</dd>
              <dt>Planifié pour</dt>
              <dd>{new Date(item.scheduled_for).toLocaleString('fr-FR')}</dd>
              {item.external_publication_id && (
                <>
                  <dt>Publication externe</dt>
                  <dd>{item.external_publication_id}</dd>
                </>
              )}
              {item.last_error && (
                <>
                  <dt>Dernière erreur</dt>
                  <dd className="queue-detail-error">{item.last_error}</dd>
                </>
              )}
            </dl>
          </Drawer.Section>

          <Drawer.Section title="Historique des tentatives">
            {attempts.length === 0 ? (
              <p>Pas encore de tentative.</p>
            ) : (
              <ul className="queue-attempts">
                {attempts.map((a) => (
                  <AttemptItem key={a.id} attempt={a} />
                ))}
              </ul>
            )}
          </Drawer.Section>
        </>
      )}
    </Drawer>
  )
}

function AttemptItem({ attempt }: { attempt: QueueAttemptResponse }) {
  return (
    <li className={`queue-attempt queue-attempt--${attempt.outcome}`}>
      <div className="queue-attempt-header">
        <span className="queue-attempt-num">#{attempt.attempt_number}</span>
        <span className="queue-attempt-outcome">{attempt.outcome}</span>
        {attempt.latency_ms !== null && attempt.latency_ms !== undefined && (
          <span className="queue-attempt-latency">{attempt.latency_ms} ms</span>
        )}
      </div>
      <div className="queue-attempt-time">
        {new Date(attempt.started_at).toLocaleString('fr-FR')}
      </div>
      {attempt.error_message && <div className="queue-attempt-error">{attempt.error_message}</div>}
    </li>
  )
}
